// src/pages/markets/context/SignalInteractionContext.ts
//
// Named context for signal hover / select / filter state.
// Wrap the chart + sidebar subtree with <SignalInteractionContext.Provider>.
// Both surfaces call useSignalInteraction() to read state and fire actions.
//
// On a page without a sidebar, instantiate useInteractionState() directly
// and call actions.select() / actions.setVisibleIds() from wherever you need.

import { createContext, useContext } from 'react';
import type { InteractionState, InteractionActions } from '../hooks/useInteractionState';

// ─── Context ──────────────────────────────────────────────────────────────────

interface SignalInteractionContextValue {
    state: InteractionState<string>;
    actions: InteractionActions<string>;
}

export const SignalInteractionContext =
    createContext<SignalInteractionContextValue | null>(null);

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current signal interaction state and actions.
 * Must be called inside a <SignalInteractionContext.Provider>.
 */
export function useSignalInteraction(): SignalInteractionContextValue {
    const ctx = useContext(SignalInteractionContext);
    if (!ctx) {
        throw new Error(
            'useSignalInteraction must be used inside <SignalInteractionContext.Provider>',
        );
    }
    return ctx;
}